import { FormikContextType, useFormik } from 'formik';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { IsAllow, PlanTypes } from '../../../redux-store/types/portfolio';
import en from '../../../lang/en-us';
import { CustomRadio } from '../../../lib/CustomRadio';
import { InvestModePayLoadType, InvestThrough, MAX_LENGTHS } from './Switch/types';
import {
    Checkbox,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import { isError, showHelperText } from './Switch/utils';
import { isAlphaNumeric, numberRegex } from '../../../utils/constants';
import { investModeValidationSchema } from './utils';
import AsyncLoader from '../../../lib/AsyncLoader';
import { TabElementSkeleton, TabElementSkeletonRect } from './ExploreMFs/mf-investment';
import { UI_KEYS } from './ExploreMFs/mf-helpers';
const { dbCommercial } = en;

interface Props {
    planType: PlanTypes;
    formik: FormikContextType<InvestModePayLoadType>;
    showData: boolean;
    setEuin?: any;
}

const initState: InvestModePayLoadType = {
    distId: '',
    subBrokerARN: '',
    subDist: '',
    euin: '',
    euinDeclarationFlag: '',
    riaCode: '',
    investThrough: null,
    euinCheck: null,
    riaFlag: UI_KEYS.RIA_CODE,
};

export function useInvestThrough(initValues = initState) {
    const formik = useFormik({
        initialValues: initValues,
        onSubmit: () => {},
        validationSchema: investModeValidationSchema,
    });

    // useEffect(() => {
    //     formik.setValues((values) => ({ ...values, ...initValues }));
    // }, [initValues]);

    const resetState = useCallback(() => {
        formik.setValues(initValues);
    }, [initValues]);

    return { formik, resetState };
}

const InvestThroughPage: React.FunctionComponent<Props> = ({
    planType = PlanTypes.R,
    formik,
    showData,
    setEuin,
}) => {
    const {
        euin,
        euinDeclarationFlag,
        investThrough,
        distId,
        subBrokerARN,
        subDist,
        riaCode,
        riaFlag,
        euinCheck: showEuin,
    } = formik.values;

    useEffect(() => {
        const _investThrough = [PlanTypes.Direct, PlanTypes.D].includes(planType)
            ? InvestThrough.direct
            : InvestThrough.arn;
        formik.setFieldValue('investThrough', _investThrough);
    }, [planType]);

    return (
        <div className="investment-mode ">
            <h3>
                <AsyncLoader
                    skelton={<TabElementSkeletonRect width={25} height={25} />}
                    data={showData}
                >
                    <img src="/images/investment-mode.svg" alt="" />
                </AsyncLoader>
                <AsyncLoader
                    skelton={<TabElementSkeleton width={160} height={35} />}
                    data={showData}
                >
                    <>{dbCommercial.exploreMfs.mfInvestment.investmentMode}</>
                </AsyncLoader>
            </h3>
            <div className="radio-div">
                <AsyncLoader
                    skelton={<TabElementSkeleton width={160} height={35} />}
                    data={showData}
                >
                    <p>{dbCommercial.exploreMfs.mfInvestment.investingThrough}</p>
                </AsyncLoader>
                <AsyncLoader
                    skelton={<TabElementSkeleton width={170} height={35} />}
                    data={showData}
                >
                    <CustomRadio
                        name="investThrough"
                        list={
                            [PlanTypes.Direct, PlanTypes.D].includes(planType)
                                ? [
                                      {
                                          label: 'Direct',
                                          value: InvestThrough.direct,
                                      },
                                      {
                                          label: 'RIA',
                                          value: InvestThrough.ria,
                                      },
                                  ]
                                : [
                                      {
                                          label: 'Distributor (ARN)',
                                          value: InvestThrough.arn,
                                      },
                                  ]
                        }
                        selectedValue={investThrough}
                        handleRadioChange={(value: string) =>
                            formik.setFieldValue('investThrough', Number(value))
                        }
                    />
                </AsyncLoader>
            </div>
            {[PlanTypes.Reggular, PlanTypes.R].includes(planType) ? (
                <>
                    <div className="inputs-wrap">
                        <AsyncLoader
                            skelton={<TabElementSkeletonRect width={160} height={50} />}
                            data={showData}
                        >
                            <TextField
                                label={dbCommercial.exploreMfs.mfInvestment.enterArn}
                                id="enter-arn"
                                variant="outlined"
                                error={isError(formik, 'distId')}
                                helperText={showHelperText(formik, 'distId')}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    if (
                                        event.target.value.length > MAX_LENGTHS.ARN_LENGTH ||
                                        !isAlphaNumeric(event.target.value)
                                    )
                                        return;
                                    formik.handleChange(event);
                                }}
                                value={distId}
                                name="distId"
                                onBlur={formik.handleBlur}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">ARN - </InputAdornment>
                                    ),
                                }}
                            />
                        </AsyncLoader>

                        <AsyncLoader
                            skelton={<TabElementSkeletonRect width={160} height={50} />}
                            data={showData}
                        >
                            <TextField
                                label={dbCommercial.exploreMfs.mfInvestment.subBrokerCode}
                                id="sub-broker-code"
                                error={isError(formik, 'subDist')}
                                helperText={showHelperText(formik, 'subDist')}
                                variant="outlined"
                                placeholder="Optional"
                                onBlur={formik.handleBlur}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    if (
                                        event.target.value.length > MAX_LENGTHS.SUB_BROKER_LENGTH ||
                                        !isAlphaNumeric(event.target.value)
                                    )
                                        return;
                                    formik.handleChange(event);
                                }}
                                value={subDist}
                                name="subDist"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start"></InputAdornment>
                                    ),
                                }}
                            />
                        </AsyncLoader>
                        <AsyncLoader
                            skelton={<TabElementSkeletonRect width={160} height={50} />}
                            data={showData}
                        >
                            <TextField
                                label={dbCommercial.exploreMfs.mfInvestment.subBrokerArnCode}
                                id="sub-broker-arn-code"
                                variant="outlined"
                                error={isError(formik, 'subBrokerARN')}
                                helperText={showHelperText(formik, 'subBrokerARN')}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    if (
                                        event.target.value.length >
                                            MAX_LENGTHS.SUB_BROKER_ARN_LENGTH ||
                                        !numberRegex.test(event.target.value)
                                    )
                                        return;
                                    formik.handleChange(event);
                                }}
                                value={subBrokerARN}
                                name="subBrokerARN"
                                onBlur={formik.handleBlur}
                                placeholder="Optional"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">ARN - </InputAdornment>
                                    ),
                                }}
                            />
                        </AsyncLoader>
                    </div>
                    <div className="radio-div">
                        <AsyncLoader
                            skelton={<TabElementSkeleton width={80} height={35} />}
                            data={showData}
                        >
                            <p>{dbCommercial.exploreMfs.mfInvestment.euinNo}</p>
                        </AsyncLoader>

                        <AsyncLoader
                            skelton={<TabElementSkeleton width={150} height={35} />}
                            data={showData}
                        >
                            <CustomRadio
                                name="showEuin"
                                list={[
                                    {
                                        label: dbCommercial.exploreMfs.mfInvestment.yes,
                                        value: IsAllow.YES,
                                    },
                                    {
                                        label: dbCommercial.exploreMfs.mfInvestment.no,
                                        value: IsAllow.No,
                                    },
                                ]}
                                selectedValue={showEuin}
                                handleRadioChange={(value: IsAllow) => {
                                    let fieldToReset =
                                        value === IsAllow.YES ? 'euinDeclarationFlag' : 'euin';
                                    formik.setFieldValue(fieldToReset, '');
                                    formik.setFieldError(fieldToReset, '');
                                    formik.setFieldTouched(fieldToReset, false);
                                    formik.setFieldValue('euinCheck', value);
                                }}
                            />
                        </AsyncLoader>
                    </div>
                    {showEuin === IsAllow.YES && (
                        <div style={{ maxWidth: 300, width: '100%', marginTop: -15 }}>
                            <TextField
                                variant="outlined"
                                name="euin"
                                value={euin}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    if (
                                        event.target.value.length > MAX_LENGTHS.EUIN_LENGTH ||
                                        !numberRegex.test(event.target.value)
                                    )
                                        return;

                                    formik.handleChange(event);
                                }}
                                error={isError(formik, 'euin')}
                                onBlur={formik.handleBlur}
                                helperText={showHelperText(formik, 'euin')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">E</InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                    )}
                    {showEuin === IsAllow.No && (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="euinDeclarationFlag"
                                        checked={euinDeclarationFlag === IsAllow.YES}
                                        onChange={(e) => {
                                            formik.setFieldValue(
                                                'euinDeclarationFlag',
                                                euinDeclarationFlag === IsAllow.YES
                                                    ? IsAllow.No
                                                    : IsAllow.YES,
                                            );
                                        }}
                                    />
                                }
                                label={
                                    <>
                                        {dbCommercial.exploreMfs.mfInvestment.iAgree}
                                        <a
                                            onClick={() => setEuin(true)}
                                            style={{ color: '#836fe0' }}
                                        >
                                            {dbCommercial.exploreMfs.mfInvestment.euinDeclaration}
                                        </a>
                                    </>
                                }
                            />
                            <div
                                className="error-text"
                                style={{
                                    color: 'red',
                                    fontSize: 12,
                                    position: 'relative',
                                    top: -15,
                                }}
                            >
                                {isError(formik, 'euinDeclarationFlag')
                                    ? formik.errors.euinDeclarationFlag
                                    : null}
                            </div>
                        </>
                    )}
                </>
            ) : investThrough === InvestThrough.ria ? (
                <TextField
                    label={dbCommercial.exploreMfs.mfInvestment.riaCode}
                    id="sub-broker-arn-code"
                    variant="outlined"
                    name="riaCode"
                    error={isError(formik, 'riaCode')}
                    helperText={showHelperText(formik, 'riaCode')}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const numberRegex = /^[0-9]*$/;
                        if (
                            event.target.value.length > MAX_LENGTHS.RIA_LENGTH ||
                            !numberRegex.test(event.target.value)
                        )
                            return;
                        formik.handleChange(event);
                    }}
                    value={riaCode}
                    onBlur={formik.handleBlur}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Select
                                    onChange={(e) =>
                                        formik.setFieldValue('riaFlag', e.target.value)
                                    }
                                    value={formik.values.riaFlag}
                                >
                                    <MenuItem value={UI_KEYS.RIA_CODE}>INA</MenuItem>
                                    <MenuItem value={UI_KEYS.INP}>INP</MenuItem>
                                </Select>
                            </InputAdornment>
                        ),
                    }}
                />
            ) : null}
        </div>
    );
};

export default InvestThroughPage;
